import { useMemo } from 'react';
import {
  Button,
  Form,
  InlineStack,
  Link,
  Panel,
  PanelSection,
  Tooltip,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { FormProvider } from '@/components/form';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import type { Integration } from '@/services/db/integration/entity';
import type { DataOutIntegration } from '@/services/db/data-out-integration/entity';
import { IntegrationStatePanel } from './integration-state-panel';
import { LoadErrorBanner } from './load-error-banner';
import { SettingsFields } from './settings-fields';
import { EventsSection } from './events-section';
import { TestConnectionButton } from './test-connection-button';
import { useDataOutConnectorForm } from '../form/use-data-out-connector-form';
import { useIntegrationsIndexHref, useIntegrationsOverviewHref } from '../hooks';

type DataOutConnectorFormProps = {
  integration: Integration;
  dataOut: DataOutIntegration | null;
  workspaceId: string;
  loadError: Error | null;
};

/**
 * The connector's settings form: schema-driven fields, event subscriptions, and the enable /
 * disconnect controls that only exist once the connector is connected.
 */
export function DataOutConnectorForm({ integration, dataOut, workspaceId, loadError }: DataOutConnectorFormProps) {
  const isConnected = dataOut != null;
  const settings = useMemo(() => integration.settings ?? [], [integration.settings]);
  const navigate = useSafeNavigate();
  const snackbars = useSnackbars();

  const indexHref = useIntegrationsIndexHref(workspaceId);
  const overviewHref = useIntegrationsOverviewHref(workspaceId);

  // Ember gates the fields on `journeys/exports/create` and Save on create-or-edit depending on
  // whether the connector exists — two separate checks, so an account that may edit but not create
  // keeps the narrower one. Controls stay visible but inert, matching Ember's read-only form.
  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission('journeys/exports/create');
  const canWrite = isConnected ? hasPermission('journeys/exports/edit') : hasPermission('journeys/exports/create');
  // Remove keeps its own gate rather than inheriting the coarser write check: the backend route
  // requires `Journeys_Exports_Delete`, so an edit-but-not-delete role would otherwise get a 403
  // surfaced as a generic failure. Ember gated Remove on its read-only flag and never checked
  // delete at all — this is a deliberate tightening.
  const canRemove = hasPermission('journeys/exports/delete');

  const { form, canSave, isSaving, isRemoving, onSubmit, onRemove } = useDataOutConnectorForm({
    integration,
    dataOut,
    settings,
    snackbars,
    navigate,
    overviewHref,
    canWrite,
  });

  const isFormDisabled = isSaving || isRemoving || isReadOnly;

  if (loadError != null) {
    return <LoadErrorBanner />;
  }

  return (
    <FormProvider {...form}>
      <Form onSubmit={onSubmit} data-test-data-out-form>
        <Panel my="300" withSectionBorders>
          {isConnected ? (
            <IntegrationStatePanel
              connectorName={integration.name}
              isFormDisabled={isFormDisabled}
              isRemoving={isRemoving}
              canRemove={canRemove}
              onRemove={onRemove}
            />
          ) : null}

          <PanelSection>
            <SettingsFields settings={settings} isConnected={isConnected} isDisabled={isFormDisabled} />
            <TestConnectionButton
              integration={integration}
              settings={settings}
              isFormDisabled={isFormDisabled}
              snackbars={snackbars}
            />
          </PanelSection>

          <EventsSection isDisabled={isFormDisabled} />
        </Panel>

        <InlineStack alignBlock="stretch" alignInline="end" gap="150">
          <Link href={indexHref} variant="secondary" isDisabled={isFormDisabled}>
            Cancel
          </Link>
          <Tooltip
            isDisabled={canSave}
            content="Please fill out the form or make changes to save the connection"
            isCustom={true}
          >
            {(context) => (
              <Button
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                type="submit"
                variant="primary"
                isDisabled={!canSave}
                isLoading={isSaving}
              >
                Save
              </Button>
            )}
          </Tooltip>
        </InlineStack>
      </Form>
    </FormProvider>
  );
}
